import { GlobeAltIcon, InformationCircleIcon, MapPinIcon, PhoneIcon } from "@heroicons/react/24/outline";
import { formatAddress } from "@utils/utils";
import { useState } from "react";

import { Button, Paragraph } from "@fronterahealth/frontera-ui-components";

import { ApiClinicListingVerificationStatusChoices } from "@api/graphql/types-and-hooks";
import { ClinicLead, usePublicVerifyListingCreate } from "@api/rest/types-and-hooks";
import { DoubleColumnContainer, FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError } from "@components/notifications/notifications";
import { ClaimView } from "@pages/Clinic/ClaimClinic";
import { VerificationPending } from "@pages/Clinic/VerificationPending";

interface ContactFormType {
  email: string;
  firstname: string;
  lastname: string;
  jobtitle: string;
  phone: string;
}

interface ClaimClinicFormProps {
  leadDetails: ClinicLead;
  setView: React.Dispatch<React.SetStateAction<ClaimView>>;
}

export const ClaimClinicForm: React.FC<ClaimClinicFormProps> = ({ leadDetails, setView }) => {
  const [verificationStatus, setVerificationStatus] = useState<string>(
    leadDetails.clinic_listing.verification_status || ApiClinicListingVerificationStatusChoices.Unverified,
  );

  const { mutateAsync } = usePublicVerifyListingCreate();

  const { formState, onSubmit, RegisteredFormInput } = useFormUtils<ContactFormType>({
    mutationFn: async (params) => {
      const response = await mutateAsync({
        data: {
          clinic_listing_id: leadDetails.clinic_listing.public_id,
          first_name: params.firstname,
          last_name: params.lastname,
          email: params.email,
          phone_number: params.phone,
          job_title: params.jobtitle,
        },
      });
      if (response.status !== 200) {
        notifyError(response.data.detail || "Failed to submit verification.");
        return;
      }
      setVerificationStatus(response.data.verification_status);
    },
    defaultValues: {
      email: "",
      firstname: "",
      lastname: "",
      jobtitle: "",
      phone: "",
    },
  });

  return (
    <>
      {verificationStatus != ApiClinicListingVerificationStatusChoices.Unverified ? (
        <VerificationPending />
      ) : (
        <div className="flex flex-col w-full items-center my-4 md:my-8">
          <div className="flex flex-col gap-4 w-full max-w-2xl px-4 sm:px-6">
            <div className="flex flex-col items-center">
              <Paragraph className="text-xl! md:text-2xl!" size="large" colorType="primary" displayType="loud">
                Verify clinic ownership
              </Paragraph>
              <Paragraph size="medium">Help us confirm you represent this clinic</Paragraph>
            </div>

            <div className="flex flex-row gap-2 items-start rounded-lg p-4 bg-yellow-50 wrap-break-word">
              <InformationCircleIcon className="size-6 shrink-0 text-yellow-800" />
              <Paragraph className="text-yellow-800!">
                We’ll review your request within 1 business day. Once approved, you will have access to all future
                leads.
              </Paragraph>
            </div>

            <div className="flex flex-col p-8 gap-6 border bg-white rounded-lg">
              <div className="flex flex-row gap-2 items-start rounded-lg p-4 bg-blue-50 wrap-break-word">
                <InformationCircleIcon className="size-6 shrink-0 text-blue-800" />
                <div className="w-full text-left">
                  <Paragraph className="text-blue-800!">
                    <span className="font-semibold">Verification required:</span> To protect clinic privacy and ensure
                    legitimate access, we'll reach out to gather proof of ownership. Please provide your information
                    below.
                  </Paragraph>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Paragraph className="text-black! text-xl!" displayType="loud">
                  {leadDetails.clinic_listing.name}
                </Paragraph>

                <div className="flex flex-row w-fit items-center gap-1 py-1 px-2 rounded-lg bg-orange-100">
                  <InformationCircleIcon className="size-5 shrink-0 text-orange-600" />
                  <Paragraph className="text-orange-600! text-sm!" displayType="loud">
                    Unclaimed Listing
                  </Paragraph>
                </div>
              </div>

              <div className="flex flex-col gap-1 mt-1">
                {leadDetails.clinic_listing.location && (
                  <div className="flex flex-row gap-2 items-center">
                    <MapPinIcon className="size-5 shrink-0 text-text-primary" />
                    <Paragraph colorType="primary">
                      {formatAddress({
                        addressLine1: leadDetails.clinic_listing.location.address_line_1,
                        addressLine2: leadDetails.clinic_listing.location.address_line_2 || "",
                        city: leadDetails.clinic_listing.location.city,
                        state: leadDetails.clinic_listing.location.state,
                        zip: leadDetails.clinic_listing.location.postal_code,
                      })}
                    </Paragraph>
                  </div>
                )}

                {leadDetails.clinic_listing.phone_number && (
                  <div className="flex flex-row gap-2 items-center">
                    <PhoneIcon className="size-5 shrink-0 text-text-primary" />
                    <Paragraph colorType="primary">{leadDetails.clinic_listing.phone_number}</Paragraph>
                  </div>
                )}

                {leadDetails.clinic_listing.website_url && (
                  <div className="flex flex-row gap-2 items-center">
                    <GlobeAltIcon className="size-5 shrink-0 text-text-primary" />
                    <Paragraph colorType="primary">{leadDetails.clinic_listing.website_url}</Paragraph>
                  </div>
                )}
              </div>

              <FormContainer onSubmit={onSubmit} noValidate>
                <div className="flex flex-col gap-4">
                  <DoubleColumnContainer>
                    <RegisteredFormInput
                      formKey="firstname"
                      inputSize="full"
                      formState={formState}
                      label="First Name"
                    />
                    <RegisteredFormInput formKey="lastname" inputSize="full" formState={formState} label="Last Name" />
                  </DoubleColumnContainer>

                  <DoubleColumnContainer>
                    <RegisteredFormInput
                      formKey="email"
                      inputSize="full"
                      formState={formState}
                      label="Email"
                      type="email"
                    />

                    <RegisteredFormInput formKey="phone" inputSize="full" formState={formState} label="Phone" />
                  </DoubleColumnContainer>

                  <RegisteredFormInput formKey="jobtitle" inputSize="full" formState={formState} label="Job Title" />
                </div>

                <div className="flex flex-row gap-4 w-full mt-6">
                  <div className="flex-1 min-w-0">
                    <Button
                      type="button"
                      className="max-w-none! w-full"
                      text="Cancel"
                      onClick={() => setView(ClaimView.LEAD_SUMMARY)}
                      variant="primary"
                      buttonStyle="border"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Button className="max-w-none! w-full" text="Submit verification request" type="submit" />
                  </div>
                </div>
              </FormContainer>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
